import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  toolCategories,
  collections,
  toolsInCollection,
  allTools,
  accentClasses,
  type Accent,
  type ToolDef,
} from "../toolRegistry";
import Avatar from "../components/ui/Avatar";
import { useOrgCollections } from "../lib/useOrgProfiles";

/**
 * Tools landing page.
 *
 * Shows people first, as cards in the same visual language as the tool cards --
 * you pick a person, then see their tools. Topic browsing is still there for
 * anyone who thinks in categories rather than colleagues.
 *
 * Searching bypasses both groupings and goes straight to matching tools: when
 * you already know what you want, being asked to guess whose collection it
 * lives in is pure friction.
 */

function ToolCard({ tool, accent }: { tool: ToolDef; accent: Accent }) {
  const a = accentClasses[accent];
  return (
    <Link
      to={tool.live ? `/tools/${tool.id}` : "#"}
      className={`ui-card p-4 h-full flex gap-3 items-start transition duration-150 hover:shadow-lg2 hover:-translate-y-0.5 ${
        !tool.live ? "opacity-55 pointer-events-none" : ""
      }`}
    >
      <span className={`w-10 h-10 shrink-0 rounded-sm2 ${a.tint} flex items-center justify-center text-lg`}>
        {tool.icon}
      </span>
      <div className="min-w-0">
        <div className="font-bold flex items-center gap-2">
          {tool.name}
          {tool.live ? (
            <span className="pill bg-brand-tint text-brand-deep">Live</span>
          ) : (
            <span className="pill bg-paper-2 text-ink-faint">Work in progress</span>
          )}
        </div>
        <p className="text-ink-soft text-[13px] mt-0.5 line-clamp-2">{tool.description}</p>
      </div>
    </Link>
  );
}

/** Small stack of tool icons, so a collection card previews its contents. */
function IconPeek({ tools, accent }: { tools: ToolDef[]; accent: Accent }) {
  const a = accentClasses[accent];
  return (
    <div className="flex -space-x-1.5">
      {tools.slice(0, 5).map((t) => (
        <span
          key={t.id}
          title={t.name}
          className={`w-7 h-7 rounded-sm2 ${a.tint} border-2 border-card flex items-center justify-center text-[13px]`}
        >
          {t.icon}
        </span>
      ))}
    </div>
  );
}

type Mode = "people" | "topic";

export default function ToolsPage({ username }: { username?: string }) {
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<Mode>("people");
  const q = query.trim().toLowerCase();

  const searchHits = useMemo(() => {
    if (!q) return [];
    return allTools.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        (t.tags ?? []).some((tag) => tag.toLowerCase().includes(q))
    );
  }, [q]);

  const liveCollections = useOrgCollections();
  const firstName = (username ?? "").trim().split(" ")[0].toLowerCase();

  // The logged-in user's own collection leads, so the common case is one click.
  const orderedCollections = useMemo(() => {
    const list = [...liveCollections];
    if (!firstName) return list;
    return list.sort((a, b) => {
      const am = a.person.toLowerCase().startsWith(firstName) ? 0 : 1;
      const bm = b.person.toLowerCase().startsWith(firstName) ? 0 : 1;
      return am - bm;
    });
  }, [firstName, liveCollections]);

  const claimed = new Set(liveCollections.flatMap((c) => c.toolIds));
  const unclaimed = allTools.filter((t) => !claimed.has(t.id));

  const subtitle = q
    ? `${searchHits.length} tool${searchHits.length === 1 ? "" : "s"} matching this search`
    : mode === "people"
    ? "Pick a collection to see what is inside."
    : "Browse by what the tool does.";

  return (
    <div>
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <h2 className="font-extrabold text-[26px] tracking-tight">Tools</h2>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex bg-paper-2 rounded-sm2 p-0.5 text-[13px] font-semibold">
            {(["people", "topic"] as Mode[]).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`px-3 py-1 rounded-sm2 transition ${
                  mode === m ? "bg-card shadow-sm text-ink" : "text-ink-faint hover:text-ink-soft"
                }`}
              >
                {m === "people" ? "By person" : "By topic"}
              </button>
            ))}
          </div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tools…"
            className="bg-card border border-line-strong rounded-sm2 px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-brand-tint"
          />
        </div>
      </div>
      <p className="text-ink-soft mb-6">{subtitle}</p>

      {q ? (
        searchHits.length === 0 ? (
          <p className="text-ink-soft text-sm">Nothing matches that search.</p>
        ) : (
          <div className="grid grid-cols-1 min-[640px]:grid-cols-2 gap-3">
            {searchHits.map((t) => (
              <ToolCard key={t.id} tool={t} accent="brand" />
            ))}
          </div>
        )
      ) : mode === "people" ? (
        <div className="grid grid-cols-1 min-[560px]:grid-cols-2 min-[980px]:grid-cols-3 gap-3">
          {orderedCollections.map((c, i) => {
            const tools = toolsInCollection(c);
            const mine = Boolean(firstName) && c.person.toLowerCase().startsWith(firstName);
            return (
              <Link
                key={c.id}
                to={`/tools/group/${c.id}`}
                className="ui-card p-4 h-full flex flex-col transition duration-150 hover:shadow-lg2 hover:-translate-y-0.5 animate-[fadeUp_.35s_ease-out_both]"
                style={{ animationDelay: `${Math.min(i, 8) * 45}ms` }}
              >
                <div className="flex items-center gap-3">
                  <Avatar initials={c.initials} accent={c.accent} photo={c.photo} alt={c.person} size="lg" shape="rounded" />
                  <div className="min-w-0">
                    <div className="font-bold flex items-center gap-2">
                      {c.label}
                      {mine ? <span className="pill bg-brand-tint text-brand-deep">You</span> : null}
                    </div>
                    <div className="text-ink-faint text-[12px]">{c.person}</div>
                  </div>
                </div>
                <div className="mt-3 pt-1 mt-auto flex items-center gap-2">
                  <IconPeek tools={tools} accent={c.accent} />
                  <span className="text-ink-soft text-[13px] ml-auto">
                    {tools.length === 0
                      ? "Nothing assigned yet"
                      : `${tools.length} tool${tools.length === 1 ? "" : "s"}`}
                  </span>
                </div>
              </Link>
            );
          })}

          {unclaimed.length > 0 && (
            <Link
              to="/tools/group/everything-else"
              className="ui-card p-4 h-full flex flex-col transition duration-150 hover:shadow-lg2 hover:-translate-y-0.5 animate-[fadeUp_.35s_ease-out_both]"
              style={{ animationDelay: `${Math.min(orderedCollections.length, 8) * 45}ms` }}
            >
              <div className="flex items-center gap-3">
                <span className="w-12 h-12 shrink-0 rounded-full bg-paper-2 text-ink-faint flex items-center justify-center text-xl">
                  ⋯
                </span>
                <div className="min-w-0">
                  <div className="font-bold">Everything else</div>
                  <div className="text-ink-faint text-[12px]">Not in anyone&rsquo;s collection</div>
                </div>
              </div>
              <div className="mt-3 pt-1 mt-auto flex items-center gap-2">
                <IconPeek tools={unclaimed} accent="info" />
                <span className="text-ink-soft text-[13px] ml-auto">
                  {unclaimed.length} tool{unclaimed.length === 1 ? "" : "s"}
                </span>
              </div>
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 min-[560px]:grid-cols-2 min-[980px]:grid-cols-3 gap-3">
          {toolCategories.map((cat, i) => (
            <Link
              key={cat.name}
              to={`/tools/group/topic-${encodeURIComponent(cat.name)}`}
              className="ui-card p-4 h-full flex flex-col transition duration-150 hover:shadow-lg2 hover:-translate-y-0.5 animate-[fadeUp_.35s_ease-out_both]"
              style={{ animationDelay: `${Math.min(i, 8) * 45}ms` }}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`w-12 h-12 shrink-0 rounded-full ${accentClasses[cat.accent].tint} flex items-center justify-center text-xl`}
                >
                  {cat.tools[0]?.icon ?? "🧰"}
                </span>
                <div className="min-w-0">
                  <div className="font-bold">{cat.name}</div>
                  <div className="text-ink-faint text-[12px]">
                    {cat.tools.length} tool{cat.tools.length === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
              <div className="mt-3 mt-auto">
                <IconPeek tools={cat.tools} accent={cat.accent} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
